import { useState, useEffect, useCallback } from 'react'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import { identify, setUserProperties, reset as resetAnalytics } from '../lib/mixpanel'
import { logger } from '../lib/logger'
import { captureError } from '../lib/errorReporting'
import type { User, Session } from '@supabase/supabase-js'

const log = logger('Auth')

interface Profile {
  id: string
  is_pro: boolean
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  created_at: string
}

interface AuthState {
  user: User | null
  session: Session | null
  profile: Profile | null
  loading: boolean
  isPro: boolean
}

function readCallbackParam(key: string): string | null {
  const search = new URLSearchParams(window.location.search)
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  return search.get(key) || hash.get(key)
}

function getOAuthCallbackError(): string | null {
  const description = readCallbackParam('error_description')
  const error = readCallbackParam('error')
  if (!description && !error) return null
  return (description || error || 'Google sign-in failed').replace(/\+/g, ' ')
}

// Check once at module load whether we're returning from an OAuth callback
// Supports both implicit/hash and PKCE/query callback shapes.
const oauthSearchParams = new URLSearchParams(window.location.search)
const oauthCallbackError = getOAuthCallbackError()
const isReturningFromOAuth =
  window.location.hash.includes('access_token') ||
  oauthSearchParams.has('code') ||
  Boolean(oauthCallbackError)

function getOAuthRedirectTo(): string {
  const origin = window.location.origin
  const host = window.location.hostname

  if (host === 'localhost' || host === '127.0.0.1') {
    return origin
  }

  if (host.endsWith('gojot.app')) {
    return 'https://gojot.app'
  }

  return origin
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    loading: true,
    isPro: false,
  })

  // Fetch user profile
  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    if (!isSupabaseConfigured) return null

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) {
      log.error('Error fetching profile:', error)
      return null
    }

    return data
  }, [])

  // Initialize auth state
  useEffect(() => {
    log.info(`Initializing auth state — isSupabaseConfigured=${isSupabaseConfigured}`)
    if (!isSupabaseConfigured) {
      log.warn('Supabase not configured — skipping auth init, setting loading=false')
      setState(prev => ({ ...prev, loading: false })) // eslint-disable-line react-hooks/set-state-in-effect -- async init
      return
    }

    // Get initial session (also processes OAuth callback hash if present)
    // Note: navigator.locks is bypassed via a no-op lock in supabase.ts,
    // so getSession() will not hang in PWA / service-worker contexts.
    // A generous safety-net timeout (15s) guards against any unknown hang;
    // it should never fire under normal operation (fetch timeout is 8s).
    const SESSION_TIMEOUT_MS = 15000
    const sessionPromise = supabase.auth.getSession()
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`getSession timed out after ${SESSION_TIMEOUT_MS}ms`)), SESSION_TIMEOUT_MS)
    )

    Promise.race([sessionPromise, timeoutPromise]).then(async ({ data: { session } }) => {
      log.info(`getSession resolved — hasSession=${!!session} hasUser=${!!session?.user}`)
      // Only strip OAuth params after we actually have a session, or after a
      // callback error we already captured. Deleting `?code=` on a failed
      // implicit/PKCE mismatch used to make Google login look like a no-op.
      const canCleanOAuthParams = Boolean(session?.user) || Boolean(oauthCallbackError)
      if (canCleanOAuthParams) {
        if (window.location.hash.includes('access_token') || window.location.hash.includes('error')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search)
        } else if (oauthSearchParams.has('code') || oauthSearchParams.has('error')) {
          window.history.replaceState(null, '', window.location.pathname)
        }
      }

      if (session?.user) {
        // Identify the user in Mixpanel as early as possible so that
        // any events tracked before onAuthStateChange fires are
        // attributed to the correct user — not an anonymous profile.
        identify(session.user.id)

        try {
          const profile = await fetchProfile(session.user.id)

          setUserProperties({
            email: session.user.email ? String(session.user.email) : undefined,
            created_at: session.user.created_at ? String(session.user.created_at) : undefined,
            is_pro: profile?.is_pro ?? false,
          })

          setState({
            user: session.user,
            session,
            profile,
            loading: false,
            isPro: profile?.is_pro ?? false,
          })
        } catch (err) {
          captureError(err, { source: 'auth.initProfile' })

          setUserProperties({
            email: session.user.email ? String(session.user.email) : undefined,
            created_at: session.user.created_at ? String(session.user.created_at) : undefined,
          })

          setState({
            user: session.user,
            session,
            profile: null,
            loading: false,
            isPro: false,
          })
        }
      } else {
        setState(prev => ({ ...prev, loading: false }))
      }
    }).catch((err) => {
      captureError(err, { source: 'auth.getSession' })
      setState(prev => ({ ...prev, loading: false }))
    })

    // Listen for auth changes — skip INITIAL_SESSION since getSession().then()
    // above already handles the initial profile fetch. This avoids duplicate
    // profile fetches that can block the Supabase client's internal session pipeline.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        log.warn('onAuthStateChange — event=%s, hasUser=%s, hasSession=%s', event, !!session?.user, !!session)
        if (event === 'INITIAL_SESSION') return

        if (session?.user) {
          try {
            const profile = await fetchProfile(session.user.id)

            // Identify the user in Mixpanel
            identify(session.user.id)
            setUserProperties({
              email: session.user.email ? String(session.user.email) : undefined,
              created_at: session.user.created_at ? String(session.user.created_at) : undefined,
              is_pro: profile?.is_pro ?? false,
            })

            setState({
              user: session.user,
              session,
              profile,
              loading: false,
              isPro: profile?.is_pro ?? false,
            })
          } catch (err) {
            captureError(err, { source: 'auth.onAuthChange' })

            // Still identify even if profile fetch fails
            identify(session.user.id)
            setUserProperties({
              email: session.user.email ? String(session.user.email) : undefined,
              created_at: session.user.created_at ? String(session.user.created_at) : undefined,
            })

            setState({
              user: session.user,
              session,
              profile: null,
              loading: false,
              isPro: false,
            })
          }
        } else {
          setState({
            user: null,
            session: null,
            profile: null,
            loading: false,
            isPro: false,
          })
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [fetchProfile])

  // Sign in with email and password
  const signInWithEmail = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      throw new Error('Supabase not configured')
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      throw error
    }
  }, [])

  // Sign up with email and password
  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      throw new Error('Supabase not configured')
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
    })

    if (error) {
      throw error
    }
  }, [])

  // Sign in with Google OAuth
  const signInWithGoogle = useCallback(async () => {
    log.info(`signInWithGoogle called — isSupabaseConfigured=${isSupabaseConfigured}`)
    if (!isSupabaseConfigured) {
      log.error('Supabase not configured — VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set at build time')
      throw new Error('Supabase not configured')
    }

    const redirectTo = getOAuthRedirectTo()
    log.info(`Google OAuth redirectTo=${redirectTo}`)

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    })

    if (error) {
      log.error('Google OAuth error:', error)
      throw error
    }
  }, [])

  // Sign out
  const signOut = useCallback(async () => {
    // Clear React state immediately so the UI updates right away
    setState({
      user: null,
      session: null,
      profile: null,
      loading: false,
      isPro: false,
    })

    // Clear Mixpanel identity
    resetAnalytics()

    if (!isSupabaseConfigured) return

    // Fire the network sign-out in the background — don't block the UI
    try {
      // Race against a short timeout so a hung request doesn't linger
      await Promise.race([
        supabase.auth.signOut({ scope: 'global' }),
        new Promise(resolve => setTimeout(resolve, 3000)),
      ])
    } catch (err) {
      captureError(err, { source: 'auth.signOut' })
    }

    // Belt-and-suspenders: explicitly remove the Supabase session from
    // localStorage.  The no-op lock can allow an in-flight token refresh to
    // write back a new session *after* signOut clears it, so we remove the
    // key manually to guarantee the session is truly gone on next reload.
    try {
      const storageKey = `sb-${new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0]}-auth-token`
      localStorage.removeItem(storageKey)
    } catch {
      // URL parsing can fail if env var is missing/invalid — safe to ignore
    }
  }, [])

  // Update password
  const updatePassword = useCallback(async (newPassword: string) => {
    if (!isSupabaseConfigured) {
      throw new Error('Supabase not configured')
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) {
      throw error
    }
  }, [])

  // Refresh profile (e.g., after payment or tab focus)
  const refreshProfile = useCallback(async () => {
    if (state.user) {
      const profile = await fetchProfile(state.user.id)
      setState(prev => ({
        ...prev,
        profile,
        isPro: profile?.is_pro ?? false,
      }))
    }
  }, [state.user, fetchProfile])

  // Verify a Stripe checkout session and refresh the profile in one step.
  // This avoids the race condition where the webhook hasn't processed yet
  // when the user is redirected back from Stripe.
  const verifyCheckoutAndRefreshProfile = useCallback(async (sessionId: string) => {
    if (!isSupabaseConfigured || !state.user) return

    try {
      const { data, error } = await supabase.functions.invoke('verify-checkout', {
        body: { sessionId },
      })

      if (!error && data?.profile) {
        log.info('Checkout verified — user upgraded to Pro')
        setState(prev => ({
          ...prev,
          profile: data.profile,
          isPro: data.profile.is_pro ?? false,
        }))
        return
      }

      log.warn('verify-checkout did not return profile, falling back to refreshProfile:', error)
    } catch (err) {
      captureError(err, { source: 'auth.verifyCheckout', extra: { sessionId } })
    }

    // Fallback: try a normal profile refresh (webhook may have processed by now)
    await refreshProfile()
  }, [state.user, refreshProfile])

  // Profile refresh on tab focus removed — it caused redundant profile
  // fetches that blocked the Supabase client's internal session pipeline,
  // preventing RPC calls from being sent. Subscription status is refreshed
  // when the user opens the settings/subscription screen instead.

  return {
    user: state.user,
    session: state.session,
    profile: state.profile,
    loading: state.loading,
    isPro: state.isPro,
    isAuthenticated: !!state.user,
    isReturningFromOAuth,
    oauthCallbackError,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    updatePassword,
    refreshProfile,
    verifyCheckoutAndRefreshProfile,
  }
}
